import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usersAPI } from '../../services/api';
import { User } from '../../types';
import './UserManagement.css';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [selectedRole, setSelectedRole] = useState<number>(3);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentLoggedUser, setCurrentLoggedUser] = useState<any>(null);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      setCurrentLoggedUser(user);
      console.log('Current logged user:', user); // Debug
    }
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      console.log('Fetching users...'); // Debug
      const response = await usersAPI.getAll();
      console.log('Users response:', response.data); // Debug
      setUsers(response.data.users);
    } catch (error: any) {
      console.error('Kullanıcılar yükleme hatası:', error);
      console.error('Error response:', error.response?.data); // Debug
      setError(error.response?.data?.message || 'Kullanıcılar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (user: User) => {
    setCurrentUser(user);
    setSelectedRole(user.role_id);
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentUser(null);
    setSelectedRole(3);
    setError('');
  };

  const handleRoleUpdate = async () => {
    if (!currentUser) return;

    // Admin, avukat hesabının rolünü değiştiremez
    if (currentLoggedUser?.role?.role_name === 'admin' && currentUser.role?.role_name === 'lawyer') {
      setError('Avukat hesabının rolünü değiştirme yetkiniz yok!');
      return;
    }

    // Admin, kullanıcıyı avukat yapamaz
    if (currentLoggedUser?.role?.role_name === 'admin' && selectedRole === 2) {
      setError('Avukat rolü atama yetkiniz yok!');
      return;
    }

    try {
      await usersAPI.updateRole(currentUser.id, selectedRole);
      setSuccess('Kullanıcı rolü güncellendi');
      setTimeout(() => {
        handleCloseModal();
        fetchUsers();
      }, 1500);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Rol güncelleme başarısız');
    }
  };

  const handleToggleActive = async (user: User) => {
    // Admin, avukat hesabına işlem yapamaz
    if (currentLoggedUser?.role?.role_name === 'admin' && user.role?.role_name === 'lawyer') {
      setError('Avukat hesabını yönetme yetkiniz yok!');
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      await usersAPI.toggleActive(user.id);
      setSuccess(`Kullanıcı ${user.is_active ? 'pasif' : 'aktif'} yapıldı`);
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Durum değiştirme başarısız');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDelete = async (id: number) => {
    const userToDelete = users.find(u => u.id === id);

    // Admin, avukat hesabını silemez
    if (currentLoggedUser?.role?.role_name === 'admin' && userToDelete?.role?.role_name === 'lawyer') {
      setError('Avukat hesabını silme yetkiniz yok!');
      setTimeout(() => setError(''), 3000);
      return;
    }

    if (!window.confirm('Bu kullanıcıyı silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await usersAPI.delete(id);
      setSuccess('Kullanıcı başarıyla silindi');
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Silme işlemi başarısız');
      setTimeout(() => setError(''), 3000);
    }
  };

  const getRoleBadgeClass = (roleName: string) => {
    switch (roleName) {
      case 'admin':
        return 'badge-admin';
      case 'lawyer':
        return 'badge-moderator';
      default:
        return 'badge-user';
    }
  };

  return (
    <div className="admin-dashboard">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <h2>
            <i className="bi bi-shield-lock-fill me-2"></i>
            Admin Panel
          </h2>
        </div>

        <nav className="sidebar-nav">
          <Link to="/admin" className="nav-item">
            <i className="bi bi-speedometer2"></i>
            Dashboard
          </Link>
          <Link to="/admin/announcements" className="nav-item">
            <i className="bi bi-megaphone"></i>
            Duyurular
          </Link>
          <Link to="/admin/gallery" className="nav-item">
            <i className="bi bi-images"></i>
            Galeri
          </Link>
          <Link to="/admin/faqs" className="nav-item">
            <i className="bi bi-question-circle"></i>
            SSS
          </Link>
          <Link to="/admin/users" className="nav-item active">
            <i className="bi bi-people"></i>
            Kullanıcılar
          </Link>
        </nav>

        <div className="sidebar-footer">
          <Link to="/" className="btn btn-secondary w-100 mb-2">
            <i className="bi bi-house-door me-2"></i>
            Ana Sayfa
          </Link>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-header">
          <div>
            <h1>Kullanıcı Yönetimi</h1>
            <p className="text-muted">Kullanıcı rollerini ve durumlarını yönetin</p>
          </div>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="alert alert-success">
            <i className="bi bi-check-circle-fill me-2"></i>
            {success}
          </div>
        )}

        {error && !showModal && (
          <div className="alert alert-danger">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {error}
          </div>
        )}

        {/* Users Table */}
        <div className="users-container">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </div>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-people" style={{ fontSize: '3rem' }}></i>
              <p className="mt-3">Henüz kullanıcı yok</p>
            </div>
          ) : (
            <div className="users-table">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: '5%' }}>ID</th>
                    <th style={{ width: '20%' }}>Ad Soyad</th>
                    <th style={{ width: '25%' }}>Email</th>
                    <th style={{ width: '15%' }}>Rol</th>
                    <th style={{ width: '10%' }}>Durum</th>
                    <th style={{ width: '15%' }}>Kayıt Tarihi</th>
                    <th style={{ width: '10%' }}>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const isCurrentUser = currentLoggedUser && user.id === currentLoggedUser.id;
                    const isLawyerUser = user.role?.role_name === 'lawyer';
                    const isAdminViewingLawyer = currentLoggedUser?.role?.role_name === 'admin' && isLawyerUser;

                    return (
                      <tr key={user.id} className={isCurrentUser ? 'current-user-row' : ''}>
                        <td>{user.id}</td>
                        <td>
                          <div className="user-name-cell">
                            <i className="bi bi-person-circle me-2"></i>
                            {user.name} {user.surname}
                            {isCurrentUser && <span className="badge badge-you ms-2">Siz</span>}
                            {isAdminViewingLawyer && <span className="badge badge-warning ms-2">Korumalı</span>}
                          </div>
                        </td>
                        <td>{user.email}</td>
                        <td>
                          <span className={`badge ${getRoleBadgeClass(user.role?.role_name || 'User')}`}>
                            {user.role?.role_name || 'User'}
                          </span>
                        </td>
                        <td>
                          <button
                            onClick={() => handleToggleActive(user)}
                            className={`badge ${user.is_active ? 'badge-success' : 'badge-secondary'}`}
                            disabled={isCurrentUser || isAdminViewingLawyer}
                          >
                            {user.is_active ? 'Aktif' : 'Pasif'}
                          </button>
                        </td>
                        <td>{new Date(user.created_at).toLocaleDateString('tr-TR')}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              onClick={() => handleOpenModal(user)}
                              className="btn btn-sm btn-primary"
                              title={isAdminViewingLawyer ? "Avukat hesabını düzenleyemezsiniz" : "Rol Değiştir"}
                              aria-label="Rol değiştir"
                              disabled={isCurrentUser || isAdminViewingLawyer}
                            >
                              <i className="bi bi-shield-check"></i>
                            </button>
                            <button
                              onClick={() => handleDelete(user.id)}
                              className="btn btn-sm btn-danger"
                              title={isAdminViewingLawyer ? "Avukat hesabını silemezsiniz" : "Sil"}
                              aria-label="Kullanıcıyı sil"
                              disabled={isCurrentUser || isAdminViewingLawyer}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Role Change Modal */}
      {showModal && currentUser && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-container modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Kullanıcı Rolünü Değiştir</h3>
              <button onClick={handleCloseModal} className="modal-close" title="Kapat" aria-label="Kapat">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="modal-body">
              {error && (
                <div className="alert alert-danger">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  {error}
                </div>
              )}

              {success && (
                <div className="alert alert-success">
                  <i className="bi bi-check-circle-fill me-2"></i>
                  {success}
                </div>
              )}

              <div className="user-info-box">
                <div className="user-info-item">
                  <strong>Kullanıcı:</strong>
                  <span>{currentUser.name} {currentUser.surname}</span>
                </div>
                <div className="user-info-item">
                  <strong>Email:</strong>
                  <span>{currentUser.email}</span>
                </div>
                <div className="user-info-item">
                  <strong>Mevcut Rol:</strong>
                  <span className={`badge ${getRoleBadgeClass(currentUser.role?.role_name || 'User')}`}>
                    {currentUser.role?.role_name || 'User'}
                  </span>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="role">Yeni Rol Seç</label>
                <select
                  id="role"
                  className="form-control"
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(parseInt(e.target.value))}
                >
                  <option value={1}>Admin</option>
                  {/* Sadece avukat rolündeki kullanıcı, başkalarını avukat yapabilir */}
                  {currentLoggedUser?.role?.role_name === 'lawyer' && (
                    <option value={2}>Avukat</option>
                  )}
                  <option value={3}>Kullanıcı</option>
                </select>
                <small className="form-text">
                  <i className="bi bi-info-circle me-1"></i>
                  {currentLoggedUser?.role?.role_name === 'lawyer'
                    ? 'Admin: Kısıtlı yetkiler | Avukat: Tüm yetkiler | Kullanıcı: Sadece görüntüleme'
                    : 'Admin: Tüm yetkiler | Kullanıcı: Sadece görüntüleme'
                  }
                </small>
              </div>

              <div className="modal-footer">
                <button type="button" onClick={handleCloseModal} className="btn btn-secondary">
                  İptal
                </button>
                <button type="button" onClick={handleRoleUpdate} className="btn btn-primary">
                  <i className="bi bi-shield-check me-2"></i>
                  Rolü Güncelle
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
